import { calcCSS } from '@/lib/calc-css';
import { cn } from '@/lib/utils';
import type { SVGProps } from 'react';
import { Ref, forwardRef, memo } from 'react';

interface SherryLogoProps extends SVGProps<SVGSVGElement> {
  icon?: boolean;
  iconOnly?: boolean;
  fill?: React.CSSProperties['fill'] | 'primary';
}

const spec = {
  iconOnly: {
    width: 49,
    height: 42,
  },
  icon: {
    width: 176,
    height: 42,
  },
  default: {
    width: 132,
    height: 42,
  },
};

const getSpec = (iconOnly?: boolean, icon?: boolean) =>
  iconOnly ? spec.iconOnly : icon ? spec.icon : spec.default;

const getRadix = (iconOnly?: boolean, icon?: boolean) => {
  const { width, height } = getSpec(iconOnly, icon);
  return width / height;
};

const getViewBox = (iconOnly?: boolean, icon?: boolean) => {
  const { width, height } = getSpec(iconOnly, icon);
  return `0 0 ${width} ${height}`;
};

const SvgSherryLogo = (
  { icon, iconOnly, className, fill, ...props }: SherryLogoProps,
  ref: Ref<SVGSVGElement>
) => {
  const _icon = iconOnly || icon;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={
        props.height
          ? calcCSS`${getRadix(iconOnly, icon)} * ${props.height}`
          : `${getRadix(iconOnly, icon)}em`
      }
      height={props.width ? undefined : '1em'}
      className={cn('inline-block', className)}
      viewBox={getViewBox(iconOnly, icon)}
      ref={ref}
      {...props}
    >
      {iconOnly ? null : _icon ? (
        <path
          fill={fill === 'primary' ? 'var(--foreground)' : 'currentColor'}
          d="M170.16 15.379h5.841l-10.64 26.033h-5.526l3.347-8.155-7.293-17.878h6.22l4.009 11.354zM141.629 33.76V15.38h5.62v1.881c1.105-1.474 2.81-2.321 5.178-2.321 1.042 0 1.705.063 2.431.314l.063 5.238c-.789-.408-1.768-.628-2.873-.628-2.999 0-4.799 1.694-4.799 4.485v9.41zM125.814 33.76V15.38h5.62v1.881c1.105-1.474 2.81-2.321 5.178-2.321 1.042 0 1.705.063 2.432.314l.063 5.238c-.79-.408-1.768-.628-2.873-.628-3 0-4.8 1.694-4.8 4.485v9.41zM113.042 34.198c-5.652 0-9.63-4.015-9.63-9.629 0-5.583 3.978-9.63 9.63-9.63 5.651 0 9.63 4.016 9.63 9.63 0 .533-.032 1.13-.127 1.788h-13.544c.442 2.133 1.925 3.356 4.041 3.356 1.61 0 2.936-.565 3.41-1.443h5.651c-.947 3.513-4.578 5.928-9.061 5.928m-4.073-11.448h8.209c-.474-2.196-1.926-3.387-4.136-3.387s-3.599 1.191-4.073 3.387M82.688 33.759V7.412h5.62v9.849c1.104-1.474 2.81-2.321 5.177-2.321 4.294 0 7.231 3.105 7.231 8.186V33.76h-5.62v-9.692c0-2.791-1.137-4.328-3.252-4.328-2.21 0-3.536 1.725-3.536 4.579v9.441zM69.208 7.569c5.747 0 10.04 3.481 10.325 8.343h-5.936c-.347-2.227-2.02-3.576-4.767-3.576-2.684 0-4.168 1.067-4.168 2.604 0 1.913 1.989 2.572 7.01 3.607 5.935 1.223 7.956 4.14 7.956 7.967 0 4.893-4.642 7.872-10.388 7.872-6.346 0-10.987-3.419-11.24-8.876h5.904c.316 2.446 2.463 4.077 5.746 4.077 2.337 0 4.073-1.16 4.073-2.823 0-2.038-1.736-2.854-6.314-3.7-5.589-1.036-8.62-3.325-8.62-8.124 0-4.611 4.61-7.371 10.42-7.371"
        />
      ) : (
        <path
          fill={fill === 'primary' ? 'var(--foreground)' : 'currentColor'}
          d="M125.466 10.904H132L120.098 40h-6.181l3.744-9.114-8.159-19.982h6.958l4.485 12.69zM93.55 31.446V10.904h6.288v2.103c1.236-1.647 3.143-2.594 5.792-2.594 1.165 0 1.907.07 2.719.35l.071 5.855c-.883-.456-1.978-.701-3.214-.701-3.355 0-5.368 1.893-5.368 5.013v10.516zM75.863 31.446V10.904h6.287v2.103c1.236-1.647 3.143-2.594 5.792-2.594 1.166 0 1.908.07 2.72.35l.07 5.855c-.882-.456-1.977-.701-3.213-.701-3.356 0-5.369 1.893-5.369 5.013v10.516zM61.571 31.937c-6.322 0-10.772-4.487-10.772-10.762 0-6.24 4.45-10.762 10.772-10.762S72.344 14.9 72.344 21.175c0 .596-.036 1.262-.142 1.998H57.05c.495 2.384 2.155 3.751 4.521 3.751 1.801 0 3.285-.63 3.815-1.612h6.322c-1.06 3.926-5.121 6.625-10.137 6.625m-4.556-12.795h9.183c-.53-2.454-2.154-3.786-4.627-3.786s-4.026 1.332-4.556 3.786M27.617 31.447V2h6.287v11.007c1.236-1.647 3.143-2.594 5.792-2.594 4.804 0 8.088 3.47 8.088 9.15v11.884h-6.286V20.614c0-3.12-1.272-4.837-3.638-4.837-2.473 0-3.956 1.928-3.956 5.118v10.552zM12.538 2.175c6.428 0 11.232 3.891 11.55 9.325h-6.64c-.389-2.49-2.26-3.997-5.334-3.997-3.002 0-4.662 1.192-4.662 2.91 0 2.138 2.225 2.874 7.841 4.031 6.64 1.367 8.9 4.627 8.9 8.904 0 5.469-5.191 8.8-11.62 8.8-7.099 0-12.29-3.822-12.573-9.922h6.605c.353 2.735 2.755 4.558 6.428 4.558 2.613 0 4.556-1.297 4.556-3.155 0-2.279-1.943-3.19-7.064-4.137-6.251-1.157-9.642-3.716-9.642-9.08 0-5.152 5.157-8.237 11.655-8.237"
        />
      )}
      {_icon && (
        <>
          <path
            fill={
              fill === 'primary' ? 'url(#sherry-logo_svg__a)' : 'currentColor'
            }
            fillRule="evenodd"
            d="m37.586 27.789.001.211c0 7.727-6.19 14-13.816 14h-9.955C6.191 42 0 35.727 0 28c0-6.897 4.932-12.635 11.414-13.789L11.412 14c0-7.727 6.192-14 13.817-14h9.954C42.81 0 49 6.273 49 14c0 6.897-4.933 12.635-11.414 13.789"
            clipRule="evenodd"
          />
          <defs>
            <linearGradient
              id="sherry-logo_svg__a"
              x1={1.361}
              x2={49.37}
              y1={37.5}
              y2={4.497}
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#1647A9" />
              <stop offset={1} stopColor="#2F6CE4" />
            </linearGradient>
          </defs>
        </>
      )}
    </svg>
  );
};
const ForwardRef = forwardRef(SvgSherryLogo);
const Memo = memo(ForwardRef);
export default Memo;
